import React, {useCallback, useEffect, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, Icon, MenuItem, Select, Switch} from '@mui/material';
import PropTypes from 'prop-types';
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import SoftInput from "../../../components/SoftInput";
import SoftButton from "../../../components/SoftButton";
import RestrictedZoneDialog from "./RestrictedZoneDialog";
import {getRestrictedZones} from "../../../api/branch/restrictedZoneApiClient";

const AddEditCameraDialog = ({open, onClose, camera, isEdit, onSubmit, cctv}) => {
    const initialFormState = {
        channel: '',
        status: '',
        adultDetectionEnabled: false,
        restrictedZoneEnabled: true,
        cameraName: '',
        configId: cctv?.configId
    }
    const [formData, setFormData] = useState(initialFormState);
    const [error, setError] = useState(null);
    const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
    const [zoneCount, setZoneCount] = useState(null);

    // Zones hang off a saved camera, so there is nothing to count until one
    // exists. Refreshed after the editor saves so the summary stays honest.
    const loadZoneCount = useCallback(() => {
        if (!isEdit || !camera?.cameraId) {
            setZoneCount(null);
            return;
        }
        getRestrictedZones(camera.cameraId)
            .then((zones) => setZoneCount((zones || []).length))
            .catch(() => setZoneCount(null));
    }, [isEdit, camera]);

    useEffect(() => {
        if (open) loadZoneCount();
    }, [open, loadZoneCount]);
    useEffect(() => {
        if (isEdit && camera) {
            setFormData({
                // Backend sends channel as a number; keep it a string for the text input
                channel: camera.channel === null || camera.channel === undefined ? '' : String(camera.channel),
                status: camera.status || 'ACTIVE',
                adultDetectionEnabled: camera.adultDetectionEnabled ?? false,
                restrictedZoneEnabled: camera.restrictedZoneEnabled ?? true,
                cameraName: camera.cameraName || '',
                configId: cctv?.configId || ''
            });
        } else if (open) {
            setFormData(initialFormState);
        }
        // Clear error when dialog opens
        setError(null);
    }, [isEdit, camera, open]);

    const handleChange = (e) => {
        setFormData({...formData, [e.target.name]: e.target.value});
        // Clear error when user makes changes
        if (error) setError(null);
    };

    const handleSwitchChange = (e) => {
        setFormData({...formData, [e.target.name]: e.target.checked});
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate required fields
        const channel = String(formData.channel ?? '').trim();
        if (!channel) {
            setError("Channel is required");
            return;
        }

        if (!/^\d+$/.test(channel)) {
            setError("Channel must contain numbers only");
            return;
        }

        if (!formData.status) {
            setError("Please select the camera status");
            return;
        }

        onSubmit({...formData, channel});
        onClose();
    };

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Camera' : 'Add Camera'}</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={0}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        Channel
                                    </SoftTypography>
                                </SoftBox>
                                <SoftInput
                                    name="channel"
                                    type="text"
                                    placeholder="Channel"
                                    value={formData.channel}
                                    onChange={handleChange}
                                    required
                                />
                            </SoftBox>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    Status
                                </SoftTypography>
                            </SoftBox>
                            <Select
                                type="select"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                label="Status"
                                displayEmpty
                            >
                                <MenuItem value="" disabled>
                                    Please select the camera status
                                </MenuItem>
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="INACTIVE">InActive</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={12}>
                            <SoftBox mb={0}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >Camera Name
                                    </SoftTypography>
                                </SoftBox>
                                <SoftInput
                                    name="cameraName"
                                    type="text"
                                    placeholder="Enter camera name (optional)"
                                    value={formData.cameraName}
                                    onChange={handleChange}
                                />
                            </SoftBox>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.adultDetectionEnabled}
                                        onChange={handleSwitchChange}
                                        name="adultDetectionEnabled"
                                        color="primary"
                                    />
                                }
                                label={
                                    <SoftTypography variant="body2" color="text">
                                        Adult detection {formData.adultDetectionEnabled ? 'enabled' : 'disabled'}
                                    </SoftTypography>
                                }
                            />
                            <SoftTypography variant="caption" color="text" sx={{display: 'block', fontSize: '0.75rem'}}>
                                Include this camera in adult (teacher) presence monitoring. Requires the branch master switch to be on.
                            </SoftTypography>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{my: 1}}/>
                            <SoftTypography component="label" variant="caption" fontWeight="bold"
                                            sx={{display: 'block', mb: 0.5}}>
                                Danger zones
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text"
                                            sx={{display: 'block', fontSize: '0.75rem', mb: 1}}>
                                Outline no-go areas — a window, a door, the main gate — on a still frame from this
                                camera. Branch staff are alerted when a child is detected inside one.
                            </SoftTypography>

                            <FormControlLabel
                                sx={{mb: 1}}
                                control={
                                    <Switch
                                        checked={formData.restrictedZoneEnabled}
                                        onChange={handleSwitchChange}
                                        name="restrictedZoneEnabled"
                                        color="primary"
                                    />
                                }
                                label={
                                    <SoftTypography variant="body2" color="text">
                                        Zone monitoring {formData.restrictedZoneEnabled ? 'enabled' : 'disabled'}
                                    </SoftTypography>
                                }
                            />

                            {isEdit && camera?.cameraId ? (
                                <SoftBox display="flex" alignItems="center" gap={1.5}>
                                    {/* type=button: this sits inside the camera form and must not submit it. */}
                                    <SoftButton
                                        type="button"
                                        size="small"
                                        variant="gradient"
                                        color={zoneCount ? 'warning' : 'secondary'}
                                        onClick={() => setZoneDialogOpen(true)}
                                    >
                                        <Icon sx={{mr: 0.5}}>highlight_alt</Icon>
                                        {zoneCount ? 'Edit danger zones' : 'Set danger zones'}
                                    </SoftButton>
                                    <SoftTypography variant="caption" color="text">
                                        {zoneCount === null
                                            ? ''
                                            : zoneCount === 0
                                                ? 'None configured yet'
                                                : `${zoneCount} configured`}
                                    </SoftTypography>
                                </SoftBox>
                            ) : (
                                <SoftTypography variant="caption" color="text" sx={{fontStyle: 'italic'}}>
                                    Save the camera first — a frame has to be captured from it before zones can be
                                    drawn.
                                </SoftTypography>
                            )}
                        </Grid>
                    </Grid>
                    {error && (
                        <div style={{color: "red", marginTop: "10px"}}>
                            {error}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <SoftButton type="submit" variant="contained" color="primary" disable={error}>
                        {isEdit ? "Update Camera" : "Add Camera"}
                    </SoftButton>
                </DialogActions>
            </form>
        </Dialog>

        <RestrictedZoneDialog
            open={zoneDialogOpen}
            onClose={() => setZoneDialogOpen(false)}
            camera={camera}
            onSaved={loadZoneCount}
        />
        </>
    );
};

AddEditCameraDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    camera: PropTypes.object,
    cctv: PropTypes.object,
    isEdit: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
};

export default AddEditCameraDialog;
